import Icon from '@xo-union/tk-component-icons';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { H4, Subtle } from '@xo-union/tk-ui-typography';
import Honeybadger from 'honeybadger-js';
import React, { useContext, useEffect, useState } from 'react';
import {
  PagesFragment,
  usePagesQuery,
  useUpdatePagesMutation,
  Wws_UpdatePageAttributes,
} from '../../generated/graphql';
import { reorder } from '../../helpers/reorder';
import { PageCardUIContext } from '../../PageCardUIContext';
import { TrackingContext } from '../../TrackingContext';
import { pagesQuery } from '../LeftContainer/graphql';
import styles from './styles.scss';

export interface Props {
  page: PagesFragment;
  addToSiteHandler?: () => void;
}

const iconPageMap: any = {
  TravelPage: 'category-trv',
  WeddingPartyPage: 'guests',
  ThingsToDoPage: 'street_view',
  QAPage: 'question',
  StoryPage: 'heart',
  PhotoPage: 'category-phb',
};

const EmptyPageCard: React.FC<Props> = ({ page, addToSiteHandler }) => {
  const { data } = usePagesQuery();
  const pages = data?.pages || [];
  const [sortedPages, setSortedPages] = useState(pages || []);
  const [updatePages] = useUpdatePagesMutation();
  const { track } = useContext(TrackingContext);
  const { setPageOpen } = useContext(PageCardUIContext);

  useEffect(() => {
    setSortedPages(pages || []);
  }, [pages]);

  const addToSite = async () => {
    const sorted = reorder(sortedPages, page?.rank || 0, sortedPages.length - 1);
    setSortedPages(sorted);
    const updated = sorted.map(({ id, title, rank, show }) => {
      if (id === page.id) {
        return { id, title, rank, show: true };
      }

      return { id, title, rank, show };
    });

    try {
      await updatePages({
        variables: {
          pages: updated as Wws_UpdatePageAttributes[],
        },
        update(cache, { data }) {
          const updatePages = data?.updatePages || [];

          cache.writeQuery({
            query: pagesQuery,
            data: {
              pages: updatePages,
            },
          });
          if (addToSiteHandler) {
            addToSiteHandler();
          }
          // makes sure the page card is open when the page become visible
          setPageOpen(page.id, true);

          const clickedPage = updatePages.find(p => p.id === page.id);

          if (clickedPage) {
            track('WWS Page Edited', {
              pageType: clickedPage.type,
              action: 'edit',
              actionName: 'visibility',
              pageVisible: clickedPage.show,
            });
          }
        },
      });
    } catch (error) {
      setSortedPages(pages);
      Honeybadger.notify(error, {
        message: 'Error updating page',
        context: {
          error,
        },
      });
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Icon name={iconPageMap[page.type]} size="md" className={styles.icon} />
        <H4 as="h2">{page.title}</H4>
      </div>
      <Subtle>{page.pageGuidance?.hiddenPageDescription}</Subtle>
      <DisplayButton className={styles.link} onClick={addToSite} data-testid={`${page.title}-add-to-site`}>
        + Add to Site
      </DisplayButton>
    </div>
  );
};

export default EmptyPageCard;
